import { useMemo, useState } from 'react'
import pick from 'lodash/pick'
import classNames from 'classnames'
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid'
import { Link } from 'react-router-dom'
import {
  CreateRequestSelectLocationsQuery,
  useCreateRequestSelectLocationsQuery,
} from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import useSession from '@/buyers/hooks/useSession'
import Box from '@/gf/components/Box'
import TextTag from '@/gf/components/next/TextTag'
import { US_CENTER } from '@/gf/modules/Map'
import { Maybe, Point } from '@/types'
import SearchInput from '@/gf/components/SearchInput'
import Dropdown from '@/gf/components/next/Dropdown'
import DropdownAction from '@/gf/components/next/DropdownAction'
import Ghost from '@/gf/components/Ghost'
import Action from '@/gf/components/Action'
import Address from '@/gf/modules/Address'
import { jobSitesFirst, pickDefaultLocation } from '../locationDefaults'

const Loading = () => (
  <>
    {[1, 2, 3, 4].map((v) => (
      <Box key={v} className="p-3">
        <Ghost className="h-6 w-2/3 block" />
        <Ghost className="h-4 w-1/2 block mt-2" />
      </Box>
    ))}
  </>
)

const LocationItem = ({
  location,
  selected,
  defaultLocationId,
  onClick,
}: {
  location: NonNullable<CreateRequestSelectLocationsQuery['defaultShippingLocation']>
  selected: boolean
  defaultLocationId: string | undefined
  onClick: () => void
}) => {
  const hasCompleteAddress =
    location.address?.lineOne &&
    location.address?.city &&
    location.address?.state &&
    location.address?.postalCode

  return (
    <Box
      className={classNames(
        'p-3 cursor-pointer shadow-base transition duration-200',
        selected ? 'border-2 border-blue-600' : 'hover:border-gray-400'
      )}
      onClick={onClick}
    >
      <h6 className="flex justify-between items-center">
        <span className="text-base font-medium">
          {location.name} {location.code && `(${location.code})`}
        </span>
        {location.id === defaultLocationId && <TextTag color="blue">Default Location</TextTag>}
      </h6>
      {hasCompleteAddress && (
        <p className="text-base">{Address.formatOneLineNoCountry(location.address)}</p>
      )}
    </Box>
  )
}

const LocationsList = ({
  selectedLocationId,
  onLocationSelected,
  onAddLocationClicked,
  orgHasLocations,
  orgMachineId,
}: {
  selectedLocationId: Maybe<string>
  onLocationSelected: (point: Point, locationId: string) => void
  onAddLocationClicked: () => void
  orgHasLocations: boolean
  orgMachineId?: Maybe<string>
}) => {
  const { orgId, user, features } = useSession()
  const client = useGqlClient()
  const [search, setSearch] = useState('')
  const [sorting, setSorting] = useState<'request_date' | 'alphabetical'>('request_date')

  const { data } = useCreateRequestSelectLocationsQuery({
    variables: {
      orgId,
      userId: user.id,
      search: search ?? '',
      orderBy:
        sorting === 'request_date'
          ? { id: 'request_date', order: 'desc' }
          : { id: 'name', order: 'asc' },
      orgMachineId: orgMachineId ?? null,
    },
    client,
  })

  const locations = useMemo(() => {
    if (search || sorting !== 'request_date') {
      return data?.org?.locations ?? []
    }

    const all = data?.org?.locations ?? []

    if (!orgMachineId) {
      const defaultLocation = data?.defaultShippingLocation ?? null
      return jobSitesFirst(all, [], defaultLocation)
    }

    // The machine's job sites lead the list and supply the default, but every
    // other location stays a legal choice: a yard or a shop (GEA-11323). The
    // org's default shipping location leads only when it is a job site
    // (GEA-7296).
    const jobSites = data?.org?.jobSites ?? []
    const defaultLocation = pickDefaultLocation(jobSites, data?.defaultShippingLocation, true)

    return jobSitesFirst(all, jobSites, defaultLocation)
  }, [data, search, sorting, orgMachineId])

  // Get the selected location and check if its address is incomplete
  const selectedLocation = useMemo(
    () => (selectedLocationId ? locations.find((loc) => loc.id === selectedLocationId) : null),
    [selectedLocationId, locations]
  )

  const hasIncompleteAddress = useMemo(
    () =>
      selectedLocation &&
      (!selectedLocation.address?.lineOne ||
        !selectedLocation.address?.city ||
        !selectedLocation.address?.state ||
        !selectedLocation.address?.postalCode),
    [selectedLocation]
  )

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-y-2">
        <div className="flex gap-x-3">
          <SearchInput
            value={search}
            onChange={(value) => setSearch(value)}
            placeholder="Type to search for a location"
            className="flex-grow"
          />

          {features.zachryIntegrationEnabled ? (
            orgHasLocations ? (
              <span
                className="flex-shrink-0 inline-block border font-normal rounded-md shadow-sm text-gray-700 border-gray-300 bg-white px-4 py-2 text-sm opacity-50 cursor-not-allowed"
                title="Locations are managed in SAP and cannot be added here."
              >
                Add Location
              </span>
            ) : (
              <span
                className="flex-shrink-0 inline-block border font-normal rounded-md shadow-sm px-4 py-2 text-sm text-white border-transparent bg-blue-600 opacity-50 cursor-not-allowed"
                title="Locations are managed in SAP and cannot be added here."
              >
                Add Location
              </span>
            )
          ) : orgHasLocations ? (
            <Action.S className="flex-shrink-0" onClick={onAddLocationClicked}>
              Add Location
            </Action.S>
          ) : (
            <Action.P className="flex-shrink-0" onClick={onAddLocationClicked} color="blue">
              Add Location
            </Action.P>
          )}

          <Dropdown
            placement="bottom-end"
            trigger={
              <DropdownAction>
                <span className="text-sm">
                  Sort: {sorting === 'request_date' ? 'Recently Used' : 'Alphabetical'}
                </span>
              </DropdownAction>
            }
          >
            <Box className="shadow-lg flex flex-col w-40 overflow-hidden">
              <button
                type="button"
                className={classNames(
                  'px-3 py-2 bg-gray-50 text-sm text-left',
                  sorting === 'alphabetical' && 'text-blue-600'
                )}
                onClick={() => setSorting('alphabetical')}
              >
                Alphabetical
              </button>
              <button
                type="button"
                className={classNames(
                  'px-3 py-2 bg-gray-50 text-sm text-left',
                  sorting === 'request_date' && 'text-blue-600'
                )}
                onClick={() => setSorting('request_date')}
              >
                Recently Used
              </button>
            </Box>
          </Dropdown>
        </div>

        {hasIncompleteAddress && (
          <Link
            to={`/locations/${selectedLocationId}?edit=true`}
            className="inline-flex items-center px-3 py-1.5 rounded-md bg-yellow-50 border border-yellow-300 text-yellow-800 text-sm font-medium hover:bg-yellow-100 transition-colors w-fit"
          >
            <ExclamationTriangleIcon className="w-4 h-4 mr-1.5 text-yellow-600" />
            Incomplete address - click to update
          </Link>
        )}
      </div>

      <div className="overflow-y-auto space-y-3">
        {data === undefined ? (
          <Loading />
        ) : locations.length === 0 ? (
          <p>No locations found {search && `for ${search}`}.</p>
        ) : (
          locations.map((l) => (
            <LocationItem
              key={l.id}
              selected={selectedLocationId === l.id}
              location={l}
              defaultLocationId={data.defaultShippingLocation?.id}
              onClick={() =>
                onLocationSelected(
                  l.address.point ? pick(l.address.point, ['lat', 'lng']) : US_CENTER,
                  l.id
                )
              }
            />
          ))
        )}
      </div>
    </div>
  )
}

export default LocationsList
